/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IInteractiveFeatureItemViewProps } from './interactive-feature-item';

/**
 * Enumerator used to make some calculations to set the proper classnames
 * using modular calculations to see if they are in a right or left column or on first or last row.
 */
enum MagicNumber {
    modularDivisor = 4,
    caseNumberZero = 0,
    caseNumberOne = 1,
    caseNumberTwo = 2
}

/**
 * Feature Item View.
 * @param thisProps - Feature Item view props.
 * @param indexItem - Feature Item view props.
 * @param itemCounter - Feature Item view props.
 * @returns Feature item View.
 * @extends {} InteractiveFeatureItem
 */
const renderMosaic = (thisProps: IInteractiveFeatureItemViewProps, indexItem?: number, itemCounter?: number): JSX.Element | null => {
    const { title, paragraph, image, links,
        interactiveFeatureCollectionMosaicContainer,
        interactiveFeatureCollectionMosaicImage,
        interactiveFeatureCollectionMosaicDetails } = thisProps;
    const indexAux = indexItem ? indexItem + MagicNumber.caseNumberOne : MagicNumber.caseNumberOne;
    const itemCounterAux = itemCounter ? itemCounter : MagicNumber.caseNumberZero;
    const modularAux = indexAux % MagicNumber.modularDivisor;
    const firstRow = (indexAux <= MagicNumber.caseNumberTwo) ? 'firstRow' : '';
    const lastRow = itemCounterAux && ((indexAux === itemCounterAux) || (indexAux === itemCounterAux - MagicNumber.caseNumberOne)) ? 'lastRow' : '';
    const onCornerRight = modularAux === MagicNumber.caseNumberZero ? 'item-corner-right' : '';
    const onCornerLeft = modularAux === MagicNumber.caseNumberOne ? 'item-corner-left' : '';
    const containerClasses = classnames('ms-interactive-feature-collection-mosaic', onCornerLeft, onCornerRight, firstRow, lastRow);

    return (
        <Node {...interactiveFeatureCollectionMosaicContainer} className={containerClasses}>
            <Node {...interactiveFeatureCollectionMosaicImage}>
                {image}
            </Node>
            <Node {...interactiveFeatureCollectionMosaicDetails}>
                {title}
                {paragraph}
                {links}
            </Node>
        </Node>
    );
};

/**
 * Feature Item View.
 * @param thisProps - Feature Item view props.
 * @returns Feature item View.
 * @extends {} InteractiveFeatureItem
 */const renderCarousel = (thisProps: IInteractiveFeatureItemViewProps): JSX.Element | null => {
    const { title, paragraph, image, links,
        interactiveFeatureCollectionCarouselContainer,
        interactiveFeatureCollectionCarouselImage,
        interactiveFeatureCollectionCarouselContent } = thisProps;
    return (
        <Node {...interactiveFeatureCollectionCarouselContainer}>
            <Node {...interactiveFeatureCollectionCarouselImage}>
                {image}
            </Node>
            <Node {...interactiveFeatureCollectionCarouselContent}>
                {title}
                {paragraph}
                {links}
            </Node>
        </Node>
    );
};

/**
 * Feature Item View.
 * @param props - Feature Item view props.
 * @returns Feature item View.
 * @extends {} InteractiveFeatureItem
 */
export const FeatureItemViewComponent: React.FC<IInteractiveFeatureItemViewProps> = props => {
    const { isMosaic, featureItem, indexItem, itemCounter } = props;
    if (isMosaic) {
        return (
            <>
                {renderMosaic(props, indexItem, itemCounter)}
            </>
        );
    }
    return (
        <Module {...featureItem}>
            {renderCarousel(props)}
        </Module>
    );
};

export default FeatureItemViewComponent;
